import type {
  CompanyGroupDetailResponse,
  GroupBaselineImportContract,
  GroupBaselineImportResponse,
} from '../../api';
import { DataTable, Panel, type Column } from '../../components';
import { useImportGroupBaseline } from '../queries';
import {
  COMPANY_BASELINE_COPY,
  GROUP_BASELINE_COPY,
  RESULT_COLUMNS,
  contractLabel,
  resultLabel,
  type MovedOnContract,
  type ResultRow,
} from './baselineImport';
import { SheetBaselineImport, type SheetImportMutation } from './SheetBaselineImport';

/**
 * The group's initial population, from the one sheet that covers all of its
 * companies: a section of the group's own page.
 *
 * The company screen's import over a wider population -- see
 * [`SheetBaselineImport`](./SheetBaselineImport.tsx) for the form and the four
 * answers, which are the same. Two things are particular to a group:
 *
 *  - **Contracts are named by company.** Two companies of a group may use the
 *    same codes, so a moved-on contract is listed under its company (from the
 *    `companies.<id>.contracts.<id>` location of the 409), and the outcome is
 *    one table per company.
 *  - **The form is offered once the group has a company.** Whether those
 *    companies have contracts is the server's to say, row by row: a sheet with
 *    nowhere to go comes back as row problems naming the Subestipulante.
 */
export function GroupBaselineImport({ group }: { group: CompanyGroupDetailResponse }) {
  const runImport = useImportGroupBaseline();
  const companyIds = group.members.map((member) => member.id);

  const mutation: SheetImportMutation<GroupBaselineImportResponse> = {
    error: runImport.error,
    data: runImport.data,
    isPending: runImport.isPending,
    reset: runImport.reset,
    run: (request, onSuccess) =>
      runImport.mutate({ groupId: group.id, companyIds, ...request }, { onSuccess }),
  };

  const companyName = (companyId: string | null) =>
    group.members.find((member) => member.id === companyId)?.name ?? '—';

  const movedOnColumns: readonly Column<MovedOnContract>[] = [
    {
      key: 'company',
      header: GROUP_BASELINE_COPY.conflictCompanyHeader,
      render: (row) => companyName(row.companyId),
    },
    {
      key: 'sentence',
      header: COMPANY_BASELINE_COPY.conflictReasonHeader,
      render: (row) => <q>{row.sentence}</q>,
    },
  ];

  return (
    <SheetBaselineImport
      copy={GROUP_BASELINE_COPY}
      hasContracts={group.members.length > 0}
      mutation={mutation}
      overrideId="group-baseline-override"
      movedOnColumns={movedOnColumns}
      renderResult={(result) => <GroupOutcome result={result} />}
    />
  );
}

/** The contracts of one company, in the order the server listed them. */
interface CompanyBlock {
  readonly companyId: string;
  readonly companyName: string;
  readonly contracts: GroupBaselineImportContract[];
}

function byCompany(contracts: readonly GroupBaselineImportContract[]): readonly CompanyBlock[] {
  const blocks: CompanyBlock[] = [];
  for (const item of contracts) {
    const block = blocks.find((candidate) => candidate.companyId === item.company_id);
    if (block === undefined) {
      blocks.push({
        companyId: item.company_id,
        companyName: item.company_name,
        contracts: [item],
      });
    } else {
      block.contracts.push(item);
    }
  }
  return blocks;
}

function GroupOutcome({ result }: { result: GroupBaselineImportResponse }) {
  const rows = (block: CompanyBlock): readonly ResultRow[] =>
    block.contracts.map((item) => ({
      key: item.contract_id,
      label: resultLabel(contractLabel(item.subestipulante, item.modulo), item.overridden),
      counts: item,
    }));

  return (
    <Panel
      title={COMPANY_BASELINE_COPY.resultTitle}
      description={GROUP_BASELINE_COPY.resultDescription}
    >
      {byCompany(result.contracts).map((block) => (
        <DataTable
          key={block.companyId}
          caption={GROUP_BASELINE_COPY.resultCaptionFor(block.companyName)}
          columnVisibility={false}
          columns={RESULT_COLUMNS}
          rows={rows(block)}
          rowKey={(row) => row.key}
        />
      ))}
      <DataTable
        caption={GROUP_BASELINE_COPY.totalCaption}
        columnVisibility={false}
        columns={RESULT_COLUMNS}
        rows={[{ key: 'total', label: GROUP_BASELINE_COPY.total, counts: result.totals }]}
        rowKey={(row) => row.key}
      />
    </Panel>
  );
}
